// ============================================================================
// 📦 src/components/SelectClient.tsx — EspoirAfriqueSas Platinum PRO 2025 Deluxe+
// 🔍 Sélection dynamique de client avec autocomplétion, avatar et highlight
// ============================================================================

import React, { useState, useEffect, useRef } from "react";
import { Search, XCircle, User } from "lucide-react";
import { api } from "../lib/axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

// ============================================================================
// 🧩 Types
// ============================================================================
interface Client {
  id: number;
  nom?: string;
  prenom?: string;
  nomEntreprise?: string;
  telephone?: string;
  email?: string;
}

interface Props {
  value?: string | number;
  onChange: (clientId: string) => void;
}

// ============================================================================
// 🧠 Composant principal
// ============================================================================
export default function SelectClient({ value, onChange }: Props) {
  const [query, setQuery] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ========================================================================
  // 🚪 Fermer la liste quand clic extérieur
  // ========================================================================
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ========================================================================
  // 🔎 Recherche dynamique
  // ========================================================================
  useEffect(() => {
    if (query.length < 2) return;
    const delay = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/clients/search?q=${encodeURIComponent(query)}&limit=10`);
        setClients(res.data?.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la recherche des clients.");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(delay);
  }, [query]);

  // ========================================================================
  // ✨ Sélection d’un client
  // ========================================================================
  const handleSelect = (id: number, nomAffiche: string) => {
    onChange(String(id));
    setQuery(nomAffiche);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange("");
  };

  // ========================================================================
  // 🎨 Générateur de couleur d’avatar stable
  // ========================================================================
  const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 45%)`;
  };

  // ========================================================================
  // 🔦 Fonction de surlignage du texte recherché
  // ========================================================================
  const highlight = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="text-emerald-400 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // ========================================================================
  // 🖥️ Rendu UI
  // ========================================================================
  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block text-sm text-gray-300 mb-1">Client</label>

      {/* Champ de recherche */}
      <div className="relative">
        <input
          type="text"
          placeholder="Rechercher un client..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 pr-9 text-sm text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {query && (
          <XCircle
            className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 hover:text-red-400 cursor-pointer"
            onClick={clear}
          />
        )}
        <Search className="absolute right-8 top-2.5 w-4 h-4 text-gray-500" />
      </div>

      {/* Liste des résultats */}
      <AnimatePresence>
        {open && (
          <motion.ul
            key="client-list"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="absolute z-50 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-xl mt-1 max-h-72 overflow-auto backdrop-blur-sm"
          >
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-400">Chargement...</li>
            )}

            {!loading && clients.length === 0 && query.length >= 2 && (
              <li className="px-3 py-2 text-sm text-gray-500 italic">
                Aucun client trouvé.
              </li>
            )}

            {!loading &&
              clients.map((c) => {
                const nomAffiche =
                  c.nomEntreprise ||
                  `${c.prenom ?? ""} ${c.nom ?? ""}`.trim() ||
                  `Client #${c.id}`;
                const avatarColor = getAvatarColor(nomAffiche);

                return (
                  <li
                    key={c.id}
                    onClick={() => handleSelect(c.id, nomAffiche)}
                    className="px-3 py-2 text-sm text-gray-200 flex items-center gap-3 cursor-pointer hover:bg-slate-800/70 transition-all"
                  >
                    {/* Avatar coloré */}
                    <div
                      className="flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: avatarColor }}
                    >
                      {nomAffiche.charAt(0).toUpperCase()}
                    </div>

                    {/* Infos du client */}
                    <div className="flex flex-col">
                      <span className="font-medium">{highlight(nomAffiche, query)}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        {c.telephone && <span>{c.telephone}</span>}
                        {c.email && (
                          <span className="truncate max-w-[120px]">{c.email}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}